import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { usePurchaseFlowPaymentIntent, usePurchaseFlowPaymentStatus, usePurchaseFlowPlaceOrder } from '../_main/contexts'

import { Color } from 'constants/assets'
import FormControlLabel from '@mui/material/FormControlLabel'
import { GRAY_900 } from 'constants/styling/theme'
import { MUICheckbox } from 'components/common/MUICheckBox'
import { Path } from 'constants/router'
import { PreferredPaymentMethodEnum } from 'constants/user'
import ReactLoading from 'react-loading'
import { ReceiptEmailInput } from 'components/common/ReceiptEmailInput'
import { SavedPaymentMethodsSelect } from 'components/common/SavedPaymentMethodsSelect'
import { SystemMessage } from 'components/common/SystemMessage'
import { PaymentIntentResult, StripeError, StripePaymentElementChangeEvent } from '@stripe/stripe-js'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import { productKindIsPreferredPaymentMethod } from 'utils/typeguards'
import styles from './PurchaseFlowPayment.module.sass'
import { usePurchaseFlowOrderMeta } from 'components/pages/PurchaseFlow/_main/contexts'
import { useEmbedConfig } from 'components/pages/PurchaseFlow/_main/contexts/EmbedConfig.context'
import { useSavedStripePaymentMethods } from 'components/contexts/SavedStripePaymentMethodsContext'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'

/** The embed has no /payment-status page to re-read the PaymentIntent, so only an accepted one passes the confirm. */
const embedPaymentError = (result?: PaymentIntentResult): StripeError | undefined => {
  // A retry after a lost response finds the payment already accepted; Stripe then reports the PaymentIntent on its error.
  const status = (result?.paymentIntent ?? result?.error?.payment_intent)?.status
  if (status === 'succeeded' || status === 'processing') return undefined
  return result?.error ?? { type: 'api_error', payment_intent: result?.paymentIntent }
}

export const PurchaseFlowPayment: FC<{ disabled?: boolean }> = ({ disabled = false }) => {

  const { t } = useTranslation('saved_payment_methods')

  // STRIPE
  const stripe = useStripe()
  const elements = useElements()
  const { clientSecret } = usePurchaseFlowPaymentIntent()

  const { selectedPaymentMethod, reference } = usePurchaseFlowOrderMeta()
  const { isEmbed } = useEmbedConfig()
  const {
    savedStripePaymentMethodsMap,
    stripePaymentMethods,
  } = useSavedStripePaymentMethods()

  const { orderHasBeenPlaced, placeOrderMutation } = usePurchaseFlowPlaceOrder()

  const {
    isStripeConfirmed,
    stripeError,
    receiptEmail,
    saveStripePaymentMethod,
    selectedSavedPaymentMethod,
    stripeInputElementsAreValid,
    setReceiptEmail,
    setIsReceiptEmailValid,
    setStripeError,
    setIsStripeProcessing,
    setStripeInputElementsAreValid,
    setIsStripeConfirmed,
    setIsStripePaymentAccepted,
    setSaveStripePaymentMethod,
    setSelectedSavedPaymentMethod,
  } = usePurchaseFlowPaymentStatus()

  const [isPaymentElementReady, setIsPaymentElementReady] = useState(false)

  const dealId = useMemo(() => placeOrderMutation.data?.data.id, [placeOrderMutation.data?.data.id])

  /**
   * @function getReturnUrl - Serializes and encodes return URL for stripe payment with provided params
   * @returns Formatted encoded URL string
   * @param params - (URLSearchParams) to be appended to URL
   */
  const getReturnUrl = useCallback((params: URLSearchParams) => {
    return encodeURI(`${window.location.origin}${Path.PAYMENT_STATUS}?${params.toString()}`)
  }, [])

  const isLoading = useMemo(() => {
    if (!stripePaymentMethods?.isSuccess) return true
    if (selectedSavedPaymentMethod === null && !isPaymentElementReady) return true
    return false
  }, [isPaymentElementReady, stripePaymentMethods, selectedSavedPaymentMethod])

  const savedPaymentMethods = useMemo(() => {
    if (!selectedPaymentMethod || !productKindIsPreferredPaymentMethod(selectedPaymentMethod)) return []
    return savedStripePaymentMethodsMap[selectedPaymentMethod] || []
  }, [selectedPaymentMethod, savedStripePaymentMethodsMap])

  const showReceiptInput = useMemo(() => {

    if (selectedSavedPaymentMethod === undefined) return false

    if (selectedSavedPaymentMethod === null) {
      if (!isPaymentElementReady) return false
      return true
    }

    if (selectedSavedPaymentMethod.type in PreferredPaymentMethodEnum) return true
    return false

  }, [isPaymentElementReady, selectedSavedPaymentMethod])

  const onElementsChange = useCallback((e: StripePaymentElementChangeEvent) => {
    if (e.complete) setStripeInputElementsAreValid(true)
    else setStripeInputElementsAreValid(false)
  }, [setStripeInputElementsAreValid])

  const confirmStripePayment = useCallback(async () => {
    if (!clientSecret) throw new Error('Payment intent was not received')
    if (!stripe || !elements) throw new Error('Stripe was not initialized')
    if (!dealId) throw new Error('Parameter dealId is missing in placeOrderResponse')

    const urlParams = new URLSearchParams({
      dealId: dealId.toString(),
      reference,
    })

    setIsStripeConfirmed(true)

    let result: PaymentIntentResult | undefined

    const returnUrl = getReturnUrl(urlParams)
    // Stripe uses this URL only to redirect; without it a saved card's 3DS challenge stays in Stripe's modal, so the
    // embed's iframe never navigates away mid-payment.
    const savedMethodReturnUrl = isEmbed ? undefined : returnUrl

    // If no saved method is used, use general confirm
    if (selectedSavedPaymentMethod === null) {
      result = await stripe.confirmPayment({
        elements,
        // 'if_required' keeps the 3DS challenge inline and skips Stripe's top-level
        // redirect to return_url — mandatory when the flow runs inside a cross-origin
        // iframe (Propstack embed, ISSUE-2006): the top redirect would otherwise kick
        // the user out of the host CRM (or hang under a sandboxed iframe).
        // The success handling below then runs in-frame.
        redirect: 'if_required',
        confirmParams: {
          return_url: returnUrl,
          receipt_email: receiptEmail,
          // Whether sripe will save this payment method
          save_payment_method: saveStripePaymentMethod,
          payment_method_data: {
            billing_details: {
              email: receiptEmail,
            },
          },
        },
      })
    }

    // If saved card payment method is to be used, use specific card confirm
    // General confirm cannot accept saved payment method
    if (selectedSavedPaymentMethod?.type === PreferredPaymentMethodEnum.CARD_PAYMENT) {
      result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedSavedPaymentMethod.stripeId,
        receipt_email: receiptEmail,
        return_url: savedMethodReturnUrl,
      })
    }

    // If saved SEPA payment method is to be used, use specific SEPA confirm
    // General confirm cannot accept saved payment method
    if (selectedSavedPaymentMethod?.type === PreferredPaymentMethodEnum.SEPA_DIRECT_DEBIT) {
      result = await stripe.confirmSepaDebitPayment(clientSecret, {
        payment_method: selectedSavedPaymentMethod.stripeId,
        receipt_email: receiptEmail,
        return_url: savedMethodReturnUrl,
      })
    }

    const error = isEmbed ? embedPaymentError(result) : result?.error

    // The embed settles in place and shows its own confirmation: /payment-status is a main-app page, and reloading
    // the iframe there would drop the embed session and its tracking context.
    if (!error && isEmbed) {
      setIsStripeProcessing(false)
      setIsStripePaymentAccepted(true)
    }

    // If stripe successfully finishes without redirect, redirect user manually to payment status page
    if (!error && !isEmbed) {
      urlParams.append('payment_intent_client_secret', clientSecret)
      window.location.replace(getReturnUrl(urlParams))
    }

    // If stripe fails, reset the process and set stripe error state
    if (error) {
      // A declined card is the client's to fix; anything else points at our integration.
      const isClientSideRefusal = error.type === 'card_error' || error.type === 'validation_error'
      const log = isClientSideRefusal ? logger.info.bind(logger) : logger.warn.bind(logger)
      log('Stripe refused the payment of a placed order', undefined, {
        context: {
          component: 'PurchaseFlowPayment',
          action: 'confirmStripePayment',
          metadata: {
            dealId,
            type: error.type,
            code: error.code,
            declineCode: error.decline_code,
            status: error.payment_intent?.status ?? null,
            savedMethod: selectedSavedPaymentMethod?.type ?? null,
          },
        },
      })
      setStripeError(error)
      setIsStripeProcessing(false)
    }

  }, [clientSecret, dealId, elements, getReturnUrl, isEmbed, receiptEmail, reference, saveStripePaymentMethod, selectedSavedPaymentMethod, setIsStripeConfirmed, setIsStripePaymentAccepted, setIsStripeProcessing, setStripeError, stripe])

  // Confirm stripe payment after order is placed
  useEffect(() => {
    if (!orderHasBeenPlaced || !clientSecret || isStripeConfirmed) return
    // Retry payment needs a Stripe error, and it re-runs this effect only by resetting the confirmed flag to false:
    // without both, a throw (also one before the confirm starts) would leave the button spinning on an unpaid order.
    confirmStripePayment().catch((error) => {
      logger.error('Could not confirm the payment of a placed order', error, {
        context: { component: 'PurchaseFlowPayment', action: 'confirmStripePayment', metadata: { dealId, isEmbed } },
      })
      setIsStripeConfirmed(true)
      setStripeError({ type: 'api_error' })
      setIsStripeProcessing(false)
    })
  }, [confirmStripePayment, clientSecret, dealId, isEmbed, isStripeConfirmed, orderHasBeenPlaced, setIsStripeConfirmed, setIsStripeProcessing, setStripeError])

  // Reset readiness and validity of payment element when method changes to saved/undefined
  useEffect(() => {
    if (selectedSavedPaymentMethod !== null) {
      if (stripeInputElementsAreValid) setStripeInputElementsAreValid(false)
      if (isPaymentElementReady) setIsPaymentElementReady(false)
    }
  }, [isPaymentElementReady, selectedSavedPaymentMethod, setIsPaymentElementReady, setStripeInputElementsAreValid, stripeInputElementsAreValid])

  return (
    <div className={classnames(styles.wrap, 'fs-exclude', { [styles.disabled]: disabled })}>

      {!!stripeError && !!dealId &&
        <SystemMessage
          variant="error"
          isRawContent
          message={t('order:step_validation.payment_declined', { orderId: dealId, reason: stripeError.message ?? '' })}
        />
      }

      {!!selectedPaymentMethod && productKindIsPreferredPaymentMethod(selectedPaymentMethod) && savedPaymentMethods.length > 0 &&
        <SavedPaymentMethodsSelect
          isNewDesign={true}
          className={styles.savedMethodsList}
          paymentMethodType={selectedPaymentMethod}
          savedPaymentMethods={savedPaymentMethods}
          selectedPaymentMethod={selectedSavedPaymentMethod}
          onSelected={(method) => setSelectedSavedPaymentMethod(method)}
        />
      }

      {showReceiptInput &&
        <ReceiptEmailInput
          isRequired={true}
          isNewDesign={true}
          className={styles.receiptEmail}
          onValidityChange={(isValid) => setIsReceiptEmailValid(isValid)}
          onValueChange={(value) => setReceiptEmail(value)}
        />
      }

      {selectedSavedPaymentMethod === null &&
        <PaymentElement
          className={
            classnames(
              styles.paymentElement,
              {
                [styles.disabled]: disabled,
                [styles.loading]: !isPaymentElementReady,
              }
            )
          }
          onReady={() => setIsPaymentElementReady(true)}
          onChange={(e) => onElementsChange(e)}
          options={{
            terms: {
              card: 'never',
              sepaDebit: 'never',
            },
            fields: {
              billingDetails: {
                email: 'never',
              },
            },
          }}
        />
      }

      {selectedSavedPaymentMethod === null && isPaymentElementReady &&
        <div className={styles.checkboxWrapper}>
          <FormControlLabel
            label={
              <Typography color={GRAY_900} fontWeight={500} variant='text-md'>
                {t('save')}
              </Typography>
            }
            onChange={(e, checked) => setSaveStripePaymentMethod(checked)}
            control={
              <MUICheckbox
                name="save-payment-method-checkbox"
                id="save-payment-method-checkbox"
                checked={saveStripePaymentMethod}
                sx={{ marginRight: '1rem', padding: 0 }}
              />
            }
          />
        </div>
      }

      {isLoading &&
        <div role="status" aria-label={t('loading_aria')}>
          <ReactLoading color={Color.GRAY_TEXT} type="cylon" />
        </div>
      }

    </div>
  )
}
